import React from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Collapse,
  Box,
  Typography,
  Button,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { parseDateTime } from 'utils/datetime';
import PaginationData from 'types/models/PaginationData';
import Order from 'types/models/Order';
import { ConsumptionReportPayloadData } from 'types/forms/ConsumptionsReportFormProps';
import ConsumptionsReportForm from 'forms/ConsumptionsReportForm';
import { getReportingOrdersTotal, getReportingOrders, getAllReportingOrders } from 'services/orderServices';
import OrderDetail from 'types/models/OrderDetail';
import 'styles/pages/ConsumptionsReportPage.scss';
import { parsePaymentMethod, parseSplitPaymentText } from 'utils/report';
import { getPaymentIdsByMethod } from 'services/paymentsServices';
import OrderBillingStatuses from 'types/enums/OrderBillingStatuses';
import { formatPhone } from 'utils/formatPhone';
import { formatMoneyAuto } from 'utils/formatMoney';
import BooleanIcon from 'components/common/BooleanIcon';
import ByMethodPaymentResult from 'types/models/ByMethodPaymentResult';
import AppContext from 'contexts/AppContext';

export default (): JSX.Element => {
  const { t, i18n } = useTranslation();

  const { setIsLoading, isLoading } = React.useContext(AppContext);
  const [currentFormData, setCurrentFormData] = React.useState<ConsumptionReportPayloadData | null>(null);
  const [total, setTotal] = React.useState<number | null>(null);
  const [paginationOrders, setPaginationOrders] = React.useState<PaginationData<Order> | null>(null);
  const [openCollapseDetail, setOpenCollapseDetail] = React.useState(0);
  const paymentIdsForSearch = React.useRef<ByMethodPaymentResult[]>([]);

  const loadData = async (page?: number) => {
    if (!currentFormData) return;
    setIsLoading(true);
    try {
      const {
        cloverPointIds,
        isCashPayment,
        isCardPayment,
        isQrPayment,
        isFromCheckingAccount,
        isPreOrder,
        ...consumptionsReportPayload
      } = currentFormData;
      const paymentsMethodsCollection = [isCashPayment, isCardPayment, isQrPayment, isFromCheckingAccount, isPreOrder];

      if (
        (cloverPointIds?.length === 0 && paymentsMethodsCollection.every((method) => method === false)) ||
        (cloverPointIds?.length === 0 && paymentsMethodsCollection.every((method) => method === true))
      ) {
        const getTotal = await getReportingOrdersTotal(consumptionsReportPayload);
        const getPaginationOrders = await getReportingOrders(
          {
            ...consumptionsReportPayload,
            isCashPayment,
            isCardPayment,
            isQrPayment,
            isFromCheckingAccount,
            isPreOrder,
          },
          page,
        );
        if (getPaginationOrders) {
          setPaginationOrders(getPaginationOrders);
        }
        setTotal(getTotal !== null ? getTotal : 0);
      } else {
        const result = await getPaymentIdsByMethod(
          {
            commerceIds: consumptionsReportPayload.commerceIds,
            cloverPointIds: cloverPointIds,
            isCardPayment: isCardPayment,
            isCashPayment: isCashPayment,
            isQrPayment: isQrPayment,
            isFromCheckingAccount: isFromCheckingAccount,
            isPreOrder,
          },
          currentFormData.dateFrom,
          currentFormData.dateTo,
        );

        const allPaymentIdsPerMethods = page && page >= 1 ? paymentIdsForSearch.current : result;

        consumptionsReportPayload.paymentIds = allPaymentIdsPerMethods;
        paymentIdsForSearch.current = allPaymentIdsPerMethods;
        if (consumptionsReportPayload.paymentIds && consumptionsReportPayload.paymentIds.length === 0) {
          setPaginationOrders({
            currentPage: 0,
            data: [],
            perPage: 0,
            total: 0,
          });
          setTotal(0);
        } else {
          const getTotal = await getReportingOrdersTotal(consumptionsReportPayload);
          const getPaginationOrders = await getReportingOrders(
            {
              ...consumptionsReportPayload,
              isCashPayment,
              isCardPayment,
              isQrPayment,
              isFromCheckingAccount,
              isPreOrder,
            },
            page,
          );
          if (getPaginationOrders) {
            setPaginationOrders(getPaginationOrders);
          }
          setTotal(getTotal !== null ? getTotal : 0);
        }
      }
    } catch (error) {
      console.log('error loadData', error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadReport = async (): Promise<void> => {
    if (!currentFormData) return;
    setIsLoading(true);
    try {
      if (paymentIdsForSearch.current.length > 0) {
        currentFormData.paymentIds = paymentIdsForSearch.current;
      }
      const result = await getAllReportingOrders(currentFormData, i18n.language);
      if (result) {
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(
          new Blob([result], {
            type: 'application/octet-stream',
          }),
        );
        a.download = 'reporte_ordenes.xlsx';
        a.click();
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (total && paginationOrders) {
      setTotal(null);
      setPaginationOrders(null);
    }
    if (paymentIdsForSearch.current.length > 0) {
      paymentIdsForSearch.current = [];
    }
    loadData();
  }, [currentFormData]);

  const handleCollapse = (id: number) => {
    if (openCollapseDetail === id) {
      setOpenCollapseDetail(0);
    } else {
      setOpenCollapseDetail(id);
    }
  };

  const getPaymentMethodText = (order: Order) => {
    if (order.refundOfOrder) {
      return t('consumptionsReport-page-paymentMethod-refund', { orderId: order.refundOfOrder });
    }

    if (order.payment?.isSplitPayment) {
      return parseSplitPaymentText(order.payment.subPayments, t);
    }

    return parsePaymentMethod(t, order);
  };

  const renderContent = (order: Order, index: number): JSX.Element => {
    return (
      <React.Fragment key={index}>
        <TableRow className={'content-table'}>
          <TableCell component="th" scope="row">
            {order.id}
          </TableCell>
          <TableCell>
            {order.luxuryBox ? `${order.luxuryBox.floor}-${order.luxuryBox.identifierOnFloor}` : '-'}
          </TableCell>
          <TableCell>{order.commerce?.name}</TableCell>
          <TableCell>{formatMoneyAuto(order.total)}</TableCell>
          <TableCell>{parseDateTime(order.localDatetime)}</TableCell>
          <TableCell>{order.guestEmail || '-'}</TableCell>
          <TableCell>{order.phone ? formatPhone(order.phone) : '-'}</TableCell>
          <TableCell>{order.user?.identificationNumber ?? '-'}</TableCell>
          <TableCell>{getPaymentMethodText(order)}</TableCell>
          <TableCell>
            {order?.firstDataPayment?.ccLastFourDigits
              ? `**** **** **** ${order.firstDataPayment.ccLastFourDigits}`
              : order?.cloverDataPayment?.cardLastFourDigits?.trim()
              ? `**** **** **** ${order.cloverDataPayment.cardLastFourDigits}`
              : order?.izipayPayment?.cardNumber
              ? `**** **** **** ${order?.izipayPayment?.cardNumber.substring(
                  order?.izipayPayment?.cardNumber.length - 4,
                )}`
              : 'No Aplica'}
          </TableCell>
          <TableCell>
            {order.event ? `${order.event.name} - ${parseDateTime(order.event.eventStart)}` : 'Sin especificar'}
          </TableCell>
          <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => handleCollapse(order.id)}>
              {order.id === openCollapseDetail ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell>
            {order?.cloverDataPayment?.device
              ? `${order?.cloverDataPayment?.device.description}, Serial: ${order?.cloverDataPayment.device.serial}`
              : order?.fiservQrDataPayment?.device
              ? `${order?.fiservQrDataPayment?.device.description}, Serial: ${order?.fiservQrDataPayment?.device.serial}`
              : order?.checkingAccountPayment?.device
              ? `${order?.checkingAccountPayment?.device.description}, Serial: ${order?.checkingAccountPayment?.device.serial}`
              : 'No Aplica'}
          </TableCell>
          <TableCell>{order.delivery ?? 'No Aplica'}</TableCell>
          <TableCell>
            {order.billingStatusId === OrderBillingStatuses.BillingSuccess
              ? t('consumptionsReport-page-invoice-process-success')
              : '-'}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
            <Collapse in={order.id === openCollapseDetail} timeout="auto" unmountOnExit={true}>
              <Box margin={1}>
                <Typography variant="h6" gutterBottom={true} component="div">
                  {t('consumptionsReport-page-detail')}
                </Typography>
                <Table size="small" aria-label="details">
                  <TableHead className="table-header">
                    <TableRow>
                      <TableCell>{t('consumptionsReport-page-product')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-amount')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-clothingSizeType')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-unitPrice')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-totalProduct')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-isBenefitProduct')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.details.map((detail: OrderDetail) => (
                      <TableRow key={detail.id}>
                        <TableCell component="th" scope="row">
                          {typeof detail.product === 'string' ? detail.product : (detail.product as any)?.name}
                        </TableCell>
                        <TableCell>{detail.quantity}</TableCell>
                        <TableCell>{detail.clothinSizeType?.description ?? '-'}</TableCell>
                        <TableCell>{formatMoneyAuto(detail.price)}</TableCell>
                        <TableCell>{formatMoneyAuto(detail.totalAmount)}</TableCell>
                        <TableCell>
                          <BooleanIcon value={detail.fullProduct?.isUsedForTomorrowBenefit} showText />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <>
      <ConsumptionsReportForm currentFormData={currentFormData} setCurrentFormData={setCurrentFormData} />
      <>
        <TableContainer className="table-container" component={Paper}>
          <Table>
            <TableHead className="table-header">
              <TableRow>
                <TableCell>{t('consumptionsReport-page-order')}</TableCell>
                <TableCell>{t('consumptionsReport-page-luxuryBox')}</TableCell>
                <TableCell>{t('consumptionsReport-page-commerce')}</TableCell>
                <TableCell>{t('consumptionsReport-page-total')}</TableCell>
                <TableCell>{t('consumptionsReport-page-createdDate')}</TableCell>
                <TableCell>{t('consumptionsReport-page-guestEmail')}</TableCell>
                <TableCell>{t('consumptionsReport-page-phone')}</TableCell>
                <TableCell>{t('consumptionsReport-page-identificationNumber')}</TableCell>
                <TableCell>{t('consumptionsReport-page-paymentMethod')}</TableCell>
                <TableCell>{t('consumptionsReport-page-lastFourDigits')}</TableCell>
                <TableCell>Evento</TableCell>
                <TableCell>{t('consumptionsReport-page-detail')}</TableCell>
                <TableCell>{t('consumptionsReport-page-clover-point')}</TableCell>
                <TableCell>{t('consumptionsReport-page-delivery')}</TableCell>
                <TableCell>{t('consumptionsReport-page-invoice-process')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginationOrders?.data && paginationOrders?.data.length > 0 ? (
                paginationOrders?.data.map((order: Order, index) => renderContent(order, index))
              ) : (
                <TableRow>
                  <TableCell>{t('consumptionsReport-page-noOperations')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {paginationOrders && (
          <>
            <TableRow>
              <TableCell>{`${t('consumptionsReport-page-operationsTotal')}: ${
                total ? formatMoneyAuto(total) : 0
              }`}</TableCell>
            </TableRow>
            <TablePagination
              component="div"
              count={paginationOrders.total}
              page={paginationOrders.currentPage - 1}
              onPageChange={(_, page: number) => {
                loadData(page + 1);
              }}
              rowsPerPage={paginationOrders.perPage}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
              }}
            />
            {paginationOrders?.data.length > 0 && (
              <Box marginTop="1rem">
                <Button
                  className="btn-50"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isLoading}
                  onClick={() => {
                    downloadReport();
                  }}
                >
                  {t('consumptionsReport-page-download')}
                </Button>
              </Box>
            )}
          </>
        )}
      </>
    </>
  );
};
